import { useId, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { Anchor, Anim, Between, LOOP, Walk, walk } from '../stage';

type P = readonly [number, number];

/** A polyline through points, as a path. Every morph below keeps the same point count. */
const poly = (pts: readonly P[]) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');

/** A cubic Bézier sampled into `n` + 1 points. */
function bez(p0: P, p1: P, p2: P, p3: P, n = 32): P[] {
  const out: P[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    out.push([a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0], a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]]);
  }
  return out;
}

/** Each point's distance along the line, as a fraction of its length. */
function fractions(pts: readonly P[]): number[] {
  const at = [0];
  for (let i = 1; i < pts.length; i++) at.push(at[i - 1]! + Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]));
  const total = at.at(-1)! || 1;
  return at.map((l) => l / total);
}

/** Walk stops that trace `pts` at an even speed from t0 to t1 — so the hand and the line it draws agree. */
function trace(pts: readonly P[], t0: number, t1: number): [number, number, number][] {
  return fractions(pts).map((f, i) => [pts[i]![0], pts[i]![1], Number((t0 + (t1 - t0) * f).toFixed(4))]);
}

/** A line laid down from its start to its end between t0 and t1 (see PenDemo for the dash trick). */
function Drawn({ d, t0, t1, className, style, children }: { d: string; t0: number; t1: number; className?: string; style?: CSSProperties; children?: ReactNode }) {
  return (
    <path className={className} style={style} d={d} pathLength={1} strokeDasharray="1 3">
      <Anim attr="stroke-dashoffset" values={[1.2, 1.2, 1, 0, 0]} times={[0, t0 - 0.001, t0, t1, 1]} />
      {children}
    </path>
  );
}

/** Things carried with the hand along a walk (a brush circle), drawn round (0, 0). */
function Follow({ stops, children }: { stops: readonly (readonly [number, number, number])[]; children: ReactNode }) {
  const { path, along, times } = walk(stops);
  return (
    <g>
      <animateMotion path={path} keyPoints={along.join(';')} keyTimes={times.join(';')} dur={`${LOOP}s`} repeatCount="indefinite" calcMode="linear" />
      {children}
    </g>
  );
}

/** An id safe inside `url(#…)`. */
function useSvgId(name: string) {
  return `${name}-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

// ── Pen modes ───────────────────────────────────────────────────────────

/** Add point: click an edge between two points, and a new point sits there. */
export function PenAddDemo() {
  return <>
    <path className="g-path" d="M36 80C76 18 164 18 204 80" />
    <Anchor x={36} y={80} />
    <Anchor x={204} y={80} />
    <g><Between from={0.4} to={0.97} />
      <path className="g-handle-line" d="M88 33.5H152" />
      <circle className="g-handle-dot" cx={88} cy={33.5} r={2} />
      <circle className="g-handle-dot" cx={152} cy={33.5} r={2} />
      <Anchor x={120} y={33.5} chosen />
    </g>
    <Walk k="pen:add" stops={[[206, 104, 0.05], [120, 33.5, 0.34], [120, 33.5, 0.97]]} />
  </>;
}

/** Remove point: click a point, it goes, and the line relaxes into the curve between its neighbours. */
export function PenRemoveDemo() {
  const peaked = 'M36 82C58 72 96 32 120 32C144 32 182 72 204 82';
  const relaxed = 'M36 82C58 72 89 67 120 67C151 67 182 72 204 82';
  return <>
    <path className="g-path" d={peaked}>
      <Anim attr="d" values={[peaked, peaked, relaxed, relaxed]} times={[0, 0.42, 0.6, 1]} />
    </path>
    <Anchor x={36} y={82} />
    <Anchor x={204} y={82} />
    <g><Between from={0} to={0.42} />
      <rect className="g-anchor" x={117.8} y={29.8} width={4.4} height={4.4}>
        <Anim attr="fill" values={['var(--panel-solid)', 'var(--panel-solid)', 'var(--accent)', 'var(--accent)']} times={[0, 0.3, 0.31, 1]} />
      </rect>
    </g>
    <Walk k="pen:remove" stops={[[206, 104, 0.05], [120, 32, 0.3], [120, 32, 0.44], [150, 40, 0.66]]} />
  </>;
}

/** Convert point: click a sharp corner and it turns smooth — handles grow out and the corner rounds. */
export function PenConvertDemo() {
  const corner = 'M36 84C36 84 120 28 120 28C120 28 204 84 204 84';
  const smooth = 'M36 84C36 84 80 28 120 28C160 28 204 84 204 84';
  const t = [0, 0.4, 0.6, 1];
  return <>
    <path className="g-path" d={corner}>
      <Anim attr="d" values={[corner, corner, smooth, smooth]} times={t} />
    </path>
    <Anchor x={36} y={84} />
    <Anchor x={204} y={84} />
    <g><Between from={0.4} to={0.97} />
      <path className="g-handle-line" d="M120 28H120"><Anim attr="d" values={['M120 28H120', 'M120 28H120', 'M80 28H160', 'M80 28H160']} times={t} /></path>
      <circle className="g-handle-dot" cy={28} r={2}><Anim attr="cx" values={[120, 120, 80, 80]} times={t} /></circle>
      <circle className="g-handle-dot" cy={28} r={2}><Anim attr="cx" values={[120, 120, 160, 160]} times={t} /></circle>
    </g>
    <Anchor x={120} y={28} chosen />
    <Walk k="pen:convert" stops={[[206, 104, 0.05], [120, 28, 0.32], [120, 28, 0.97]]} />
  </>;
}

// ── Draw modes ──────────────────────────────────────────────────────────

/** Shape assist: draw a rough rectangle, hold at the end, and it snaps into a clean one. */
export function DrawShapeDemo() {
  const cx = 120, cy = 56, a = 42, b = 30;
  const N = 60;
  const rough: P[] = [];
  const clean: P[] = [];
  for (let i = 0; i <= N; i++) {
    const s = i / N;
    // Round the rectangle from the top edge, a little past the left of its middle.
    const th = -Math.PI * 0.62 + s * Math.PI * 2;
    const c = Math.cos(th), si = Math.sin(th);
    const box = 1 / Math.max(Math.abs(c) / a, Math.abs(si) / b);
    const soft = 1 / (Math.abs(c / a) ** 3.2 + Math.abs(si / b) ** 3.2) ** (1 / 3.2);
    const wob = 2.2 * Math.sin(s * Math.PI * 2 * 5 + 1) + 1.4 * Math.sin(s * Math.PI * 2 * 9 + 2) + (s > 0.9 ? (s - 0.9) * 40 : 0);
    rough.push([cx + c * (soft + wob), cy + si * (soft + wob)]);
    clean.push([cx + c * box, cy + si * box]);
  }
  const hold = rough.at(-1)!;
  const t = [0, 0.64, 0.72, 1];
  return <>
    <Drawn className="g-path-new" d={poly(rough)} t0={0.1} t1={0.5}>
      <Anim attr="d" values={[poly(rough), poly(rough), poly(clean), poly(clean)]} times={t} />
    </Drawn>
    <g><Between from={0.72} to={0.97} />
      {[[cx - a, cy - b], [cx + a, cy - b], [cx + a, cy + b], [cx - a, cy + b]].map(([x, y]) => <Anchor key={`${x},${y}`} x={x!} y={y!} />)}
    </g>
    <Walk k="draw:shape" stops={[[212, 104, 0.02], ...trace(rough, 0.1, 0.5), [hold[0], hold[1], 0.64], [206, 100, 0.84]]} />
  </>;
}

/** Pencil: the line goes exactly where the hand goes. */
export function DrawPencilDemo() {
  const pts: P[] = [];
  for (let i = 0; i <= 120; i++) {
    const s = i / 120;
    const ph = s * Math.PI * 2 * 3.25;
    pts.push([46 + 136 * s - 11 * Math.sin(ph), 60 - 18 * Math.cos(ph) * (0.7 + 0.3 * s) + 8 * s]);
  }
  return <>
    <Drawn className="g-path-new" d={poly(pts)} t0={0.1} t1={0.66} />
    <Walk k="draw:pencil" stops={[[40, 100, 0.02], ...trace(pts, 0.1, 0.66), [pts.at(-1)![0], pts.at(-1)![1], 0.97]]} />
  </>;
}

/** Fill brush: paint two fat strokes; where they touch they become one filled shape. */
export function DrawBlobDemo() {
  const one = bez([46, 78], [66, 36], [98, 34], [124, 58], 28);
  const two = bez([198, 34], [184, 74], [150, 86], [118, 64], 28);
  const edge: CSSProperties = { fill: 'none', stroke: 'var(--accent)', strokeWidth: 21, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paint: CSSProperties = { fill: 'none', stroke: 'color-mix(in srgb, var(--accent) 26%, var(--panel-solid))', strokeWidth: 18, strokeLinecap: 'round', strokeLinejoin: 'round' };
  // Every rim first, then every fill: the fills cover the rims where the strokes overlap,
  // so what is left is one outline round the joined shape.
  return <>
    <Drawn style={edge} d={poly(one)} t0={0.08} t1={0.3} />
    <Drawn style={edge} d={poly(two)} t0={0.4} t1={0.64} />
    <Drawn style={paint} d={poly(one)} t0={0.08} t1={0.3} />
    <Drawn style={paint} d={poly(two)} t0={0.4} t1={0.64} />
    <Walk k="draw:blob" stops={[[30, 104, 0.01], ...trace(one, 0.08, 0.3), [124, 58, 0.31], ...trace(two, 0.4, 0.64), [118, 64, 0.97]]} />
  </>;
}

/** Smooth: brush along a jagged line and it softens behind the brush. */
export function DrawSmoothDemo() {
  const xs: number[] = [];
  for (let x = 30; x <= 210; x += 10) xs.push(x);
  const mid = (x: number) => 58 + 7 * Math.sin(((x - 30) / 180) * Math.PI * 2);
  const jag = xs.map((x, i): P => [x, mid(x) + (i % 2 ? 12 : -12) * (i === 0 || i === xs.length - 1 ? 0 : 1)]);
  const t0 = 0.12, t1 = 0.7, r = 16;
  const frames = 16;
  const values: string[] = [];
  const times: number[] = [];
  for (let k = 0; k <= frames; k++) {
    const bx = 22 + (218 - 22) * (k / frames);
    values.push(poly(jag.map(([x, y]) => {
      const f = Math.min(1, Math.max(0, (bx - x + r) / (2 * r)));
      return [x, y + (mid(x) - y) * f] as P;
    })));
    times.push(Number((t0 + (t1 - t0) * (k / frames)).toFixed(4)));
  }
  const stops: [number, number, number][] = [[22, mid(30), t0], [218, mid(210), t1]];
  return <>
    <path className="g-path" d={values[0]!}>
      <Anim attr="d" values={[values[0]!, ...values, values.at(-1)!]} times={[0, ...times, 1]} />
    </path>
    <g><Between from={t0 - 0.04} to={t1 + 0.03} />
      <Follow stops={[[120, 104, 0.02], [22, mid(30), t0], [22, mid(30), t0], [218, mid(210), t1], [218, 100, 0.9]]}>
        <circle className="g-brush" r={r} />
      </Follow>
    </g>
    <Walk k="draw:smooth" stops={[[120, 104, 0.02], ...stops, [218, 100, 0.9]]} />
  </>;
}

/** Erase: brush over part of a line and that stretch is gone, leaving two lines. */
export function DrawEraseDemo() {
  const pts: P[] = [];
  for (let x = 30; x <= 210; x += 3) pts.push([x, 58 - 18 * Math.sin(((x - 30) / 180) * Math.PI * 2)]);
  const f = fractions(pts);
  const at = (x: number) => f[Math.round((x - 30) / 3)]!;
  const y = (x: number) => pts[Math.round((x - 30) / 3)]![1];
  const r = 10, from = 102, to = 144, t0 = 0.22, t1 = 0.58;
  const start = at(from - r);
  const gap = (bx: number) => at(bx + r) - start;
  const dash = (g: number) => `${start} ${g} 2`;
  const rub = pts.filter(([x]) => x >= from && x <= to);
  return <>
    <path className="g-path" d={poly(pts)} pathLength={1} strokeDasharray={dash(0)}>
      <Anim attr="stroke-dasharray" values={[dash(0), dash(0), dash(gap(from)), dash(gap(to)), dash(gap(to))]} times={[0, t0 - 0.02, t0, t1, 1]} />
    </path>
    <g><Between from={0.62} to={0.97} /><Anchor x={from - r} y={y(from - r)} /><Anchor x={to + r} y={y(to + r)} /></g>
    <g><Between from={t0 - 0.04} to={t1 + 0.02} />
      <Follow stops={[[206, 104, 0.04], ...trace(rub, t0, t1), [200, 30, 0.8]]}>
        <circle className="g-brush" r={r} />
      </Follow>
    </g>
    <Walk k="draw:erase" stops={[[206, 104, 0.04], ...trace(rub, t0, t1), [200, 30, 0.8]]} />
  </>;
}

/** Join: click between two loose ends and they meet. */
export function DrawJoinDemo() {
  return <>
    <path className="g-path" d="M28 82C48 34 80 34 102 56" />
    <path className="g-path" d="M138 56C160 78 192 78 212 30" />
    <g><Between from={0} to={0.42} /><Anchor x={102} y={56} /><Anchor x={138} y={56} /></g>
    <Drawn className="g-path-new" d="M102 56C112 66 128 66 138 56" t0={0.42} t1={0.52} />
    <Walk k="draw:join" stops={[[206, 104, 0.05], [120, 62, 0.34], [120, 62, 0.97]]} />
  </>;
}

// ── Width ───────────────────────────────────────────────────────────────

/** Width: drag out from a point on an even stroke and it swells there. */
export function WidthDemo() {
  const cy = 60;
  const xs: number[] = [];
  for (let x = 34; x <= 206; x += 4) xs.push(x);
  const shape = (bulge: number) => {
    const w = (x: number) => 3 + bulge * Math.exp(-(((x - 120) / 30) ** 2));
    const top = xs.map((x): P => [x, cy - w(x)]);
    const bottom = xs.map((x): P => [x, cy + w(x)]).reverse();
    return `${poly([...top, ...bottom])}Z`;
  };
  const even = shape(0), swelled = shape(15);
  const t = [0, 0.26, 0.62, 1];
  return <>
    <path className="g-new" d={even}>
      <Anim attr="d" values={[even, even, swelled, swelled]} times={t} />
    </path>
    <g><Between from={0.2} to={0.97} />
      <path className="g-handle-line" d="M120 57V63"><Anim attr="d" values={['M120 57V63', 'M120 57V63', 'M120 42V78', 'M120 42V78']} times={t} /></path>
      <circle className="g-handle-dot" cx={120} r={2}><Anim attr="cy" values={[57, 57, 42, 42]} times={t} /></circle>
      <circle className="g-handle-dot" cx={120} r={2}><Anim attr="cy" values={[63, 63, 78, 78]} times={t} /></circle>
      <Anchor x={120} y={cy} chosen />
    </g>
    <Walk k="width" stops={[[210, 104, 0.04], [120, 57, 0.22], [120, 57, 0.26], [120, 42, 0.62], [120, 42, 0.97]]} />
  </>;
}

// ── Cut modes ───────────────────────────────────────────────────────────

/** Eraser: rub across a filled shape and the bite the brush covered is gone. */
export function CutEraseDemo() {
  const mask = useSvgId('g-erase');
  const clip = useSvgId('g-erase-clip');
  // Rubbed back and forth, as an eraser is: the strokes overlap into one bite out of the top.
  const zig: P[] = [[100, 14], [108, 52], [118, 16], [126, 56], [136, 18], [144, 50], [150, 16]];
  const rub: P[] = zig.flatMap((p, i) => (i ? bez(zig[i - 1]!, zig[i - 1]!, p, p, 8).slice(1) : [p]));
  const d = poly(rub);
  const r = 10, t0 = 0.12, t1 = 0.6;
  const shape = { x: 64, y: 30, width: 112, height: 60, rx: 8 };
  return <>
    <defs>
      <clipPath id={clip}><rect {...shape} /></clipPath>
      <mask id={mask} maskUnits="userSpaceOnUse" x={0} y={0} width={240} height={112}>
        <rect width={240} height={112} fill="white" />
        <Drawn d={d} t0={t0} t1={t1} style={{ fill: 'none', stroke: 'black', strokeWidth: r * 2, strokeLinecap: 'round', strokeLinejoin: 'round' }} />
      </mask>
    </defs>
    <rect className="g-obj" {...shape} mask={`url(#${mask})`} />
    {/* The new edges the eraser leaves: a rim just wider than the brush, inside the shape, with the bite itself masked out. */}
    <g clipPath={`url(#${clip})`}>
      <g mask={`url(#${mask})`}>
        <Drawn d={d} t0={t0} t1={t1} style={{ fill: 'none', stroke: 'var(--accent)', strokeWidth: r * 2 + 3, strokeLinecap: 'round', strokeLinejoin: 'round' }} />
      </g>
    </g>
    <g><Between from={t0 - 0.04} to={t1 + 0.02} />
      <Follow stops={[[210, 104, 0.02], ...trace(rub, t0, t1), [204, 24, 0.8]]}><circle className="g-brush" r={r} /></Follow>
    </g>
    <Walk k="cut:erase" stops={[[210, 104, 0.02], ...trace(rub, t0, t1), [204, 24, 0.8]]} />
  </>;
}

/** Knife: draw a line across a shape and it falls into two pieces. */
export function CutKnifeDemo() {
  const cx = 120, cy = 56, r = 34;
  const dir: P = [-0.28, 0.96];
  const n: P = [0.96, 0.28];
  const A: P = [cx + dir[0] * r, cy + dir[1] * r];
  const B: P = [cx - dir[0] * r, cy - dir[1] * r];
  const right = `M${B[0].toFixed(2)} ${B[1].toFixed(2)}A${r} ${r} 0 0 1 ${A[0].toFixed(2)} ${A[1].toFixed(2)}Z`;
  const left = `M${A[0].toFixed(2)} ${A[1].toFixed(2)}A${r} ${r} 0 0 1 ${B[0].toFixed(2)} ${B[1].toFixed(2)}Z`;
  const k = (y: number): P => [cx + ((y - cy) * dir[0]) / dir[1], y];
  const start = k(10), end = k(102);
  const t0 = 0.14, t1 = 0.52, t = [0, 0.56, 0.7, 1];
  const drift = 6;
  const piece = (d: string, s: number) => (
    <g>
      <animateTransform attributeName="transform" type="translate" values={`0 0;0 0;${(n[0] * drift * s).toFixed(2)} ${(n[1] * drift * s).toFixed(2)};${(n[0] * drift * s).toFixed(2)} ${(n[1] * drift * s).toFixed(2)}`} keyTimes={t.join(';')} dur={`${LOOP}s`} repeatCount="indefinite" calcMode="linear" />
      <path className="g-new" d={d}><Between from={0.56} to={0.97} /></path>
    </g>
  );
  return <>
    <circle className="g-obj" cx={cx} cy={cy} r={r}><Between from={0} to={0.56} /></circle>
    {piece(left, -1)}
    {piece(right, 1)}
    <Drawn className="g-path-new" d={`M${start[0].toFixed(1)} ${start[1]}L${end[0].toFixed(1)} ${end[1]}`} t0={t0} t1={t1}>
      <Anim attr="opacity" values={[1, 1, 0, 0]} times={[0, 0.56, 0.64, 1]} />
    </Drawn>
    <Walk k="cut:knife" stops={[[206, 20, 0.04], [start[0], start[1], t0], [end[0], end[1], t1], [end[0], end[1], 0.6], [196, 96, 0.8]]} />
  </>;
}

/** Guides for the paths group, by key (see ../index.ts). */
export const PATH_DEMOS: Record<string, ComponentType> = {
  'pen:add': PenAddDemo,
  'pen:remove': PenRemoveDemo,
  'pen:convert': PenConvertDemo,
  draw: DrawShapeDemo,
  'draw:shape': DrawShapeDemo,
  'draw:pencil': DrawPencilDemo,
  'draw:blob': DrawBlobDemo,
  'draw:smooth': DrawSmoothDemo,
  'draw:erase': DrawEraseDemo,
  'draw:join': DrawJoinDemo,
  width: WidthDemo,
  cut: CutEraseDemo,
  'cut:erase': CutEraseDemo,
  'cut:knife': CutKnifeDemo,
};
